import { numberToLetter } from './utils'

const hasDisplayOrder = (entry) =>
    entry?.displayOrder !== undefined && entry?.displayOrder !== null

const getFallbackDisplayOrder = (packs, items) => {
    const entries = []
    ;(packs || []).forEach((pack, arrayIndex) => {
        entries.push({
            type: 'pack',
            arrayIndex,
            displayOrder: arrayIndex,
            key: `pack-${arrayIndex}`,
        })
    })
    ;(items || []).forEach((item, arrayIndex) => {
        entries.push({
            type: 'item',
            arrayIndex,
            displayOrder: (packs?.length || 0) + arrayIndex,
            key: `item-${arrayIndex}`,
        })
    })
    return entries
}

export const getSelectionLetter = (unifiedIndex) => numberToLetter(unifiedIndex)

export const buildUnifiedSelections = (packs = [], items = []) => {
    const packList = packs || []
    const itemList = items || []

    const anyHasDisplayOrder =
        packList.some(hasDisplayOrder) || itemList.some(hasDisplayOrder)

    if (!anyHasDisplayOrder && packList.length + itemList.length > 0) {
        return getFallbackDisplayOrder(packList, itemList).map(
            (entry, unifiedIndex) => ({
                ...entry,
                letter: getSelectionLetter(unifiedIndex),
            })
        )
    }

    const entries = []
    packList.forEach((pack, arrayIndex) => {
        entries.push({
            type: 'pack',
            arrayIndex,
            displayOrder: hasDisplayOrder(pack)
                ? pack.displayOrder
                : arrayIndex,
            key: `pack-${arrayIndex}`,
        })
    })
    itemList.forEach((item, arrayIndex) => {
        entries.push({
            type: 'item',
            arrayIndex,
            displayOrder: hasDisplayOrder(item)
                ? item.displayOrder
                : packList.length + arrayIndex,
            key: `item-${arrayIndex}`,
        })
    })

    return entries
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((entry, unifiedIndex) => ({
            ...entry,
            letter: getSelectionLetter(unifiedIndex),
        }))
}

export const getNextDisplayOrder = (packs = [], items = []) => {
    const all = [...(packs || []), ...(items || [])]
    if (!all.length) return 0
    const orders = all
        .map((e) => e?.displayOrder)
        .filter((o) => o !== undefined && o !== null)
    if (!orders.length) return all.length
    return Math.max(...orders) + 1
}

export const applyDisplayOrders = (packs, items, unifiedOrder) => {
    const newPacks = [...(packs || [])]
    const newItems = [...(items || [])]

    unifiedOrder.forEach((entry, position) => {
        if (entry.type === 'pack') {
            newPacks[entry.arrayIndex] = {
                ...newPacks[entry.arrayIndex],
                displayOrder: position,
            }
        } else {
            newItems[entry.arrayIndex] = {
                ...newItems[entry.arrayIndex],
                displayOrder: position,
            }
        }
    })

    return { packs: newPacks, items: newItems }
}

export const backfillDisplayOrders = (packs = [], items = []) => {
    const packList = packs || []
    const itemList = items || []
    const anyHasDisplayOrder =
        packList.some(hasDisplayOrder) || itemList.some(hasDisplayOrder)

    if (anyHasDisplayOrder) {
        return { packs: packList, items: itemList }
    }

    const unified = getFallbackDisplayOrder(packList, itemList)
    return applyDisplayOrders(packList, itemList, unified)
}

export const processChannelSelectionsForSubmit = (channelData) => {
    if (!channelData) return channelData

    const packs = [...(channelData.packs || [])]
    const items = [...(channelData.items || [])]
    const unified = buildUnifiedSelections(packs, items)

    unified.forEach((entry, position) => {
        const letter = getSelectionLetter(position)
        if (entry.type === 'pack') {
            packs[entry.arrayIndex] = {
                ...packs[entry.arrayIndex],
                displayOrder: position,
                shortName: letter,
            }
        } else {
            items[entry.arrayIndex] = {
                ...items[entry.arrayIndex],
                displayOrder: position,
                shortName: letter,
            }
        }
    })

    return { ...channelData, packs, items }
}

export const getOrderedSelectionsForLive = (
    packs = [],
    items = [],
    displayItems
) => {
    if (displayItems?.length) {
        return [...displayItems]
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map((entry) => ({
                type: entry.type === 'pack' ? 'pack' : 'item',
                entry,
            }))
    }

    return buildUnifiedSelections(packs, items).map((entry) => ({
        type: entry.type,
        entry:
            entry.type === 'pack'
                ? packs[entry.arrayIndex]
                : items[entry.arrayIndex],
    }))
}

export const sortBySelectionOrder = (
    data,
    orderedSelections,
    key = 'shortName'
) => {
    const orderMap = new Map(
        orderedSelections.map((s, i) => [s.entry.shortName, i])
    )
    return [...data].sort(
        (a, b) =>
            (orderMap.get(a[key]) ?? Infinity) -
            (orderMap.get(b[key]) ?? Infinity)
    )
}
